import React, { useState } from 'react';
import { Play } from 'lucide-react';
import { startCoreSkillSession } from '../../api/dashboardApi';

function InlineCoreSkills({ options, childId, onSessionStarted, activeDashboardSession }) {
  const [loadingId, setLoadingId] = useState('');
  const [error, setError] = useState('');
  const [selectedId, setSelectedId] = useState('');

  if (!Array.isArray(options) || options.length === 0) return null;

  const handleStart = async (e, lessonId) => {
    e.stopPropagation();
    if (loadingId) return;
    if (activeDashboardSession?.lessonId && activeDashboardSession.lessonId !== lessonId) {
      return;
    }
    setError('');
    setLoadingId(lessonId);
    try {
      const session = await startCoreSkillSession(childId, lessonId, 'dashboard_progress');
      onSessionStarted?.({
        lessonId: String(session?.lessonId ?? lessonId),
        sessionId: String(session?.sessionId ?? ''),
        source: session?.source ?? 'dashboard_progress',
      });
    } catch (err) {
      const blockedSession =
        err?.response?.data?.activeSession || err?.response?.data?.message?.activeSession;
      if (blockedSession?.lessonId && blockedSession?.sessionId) {
        onSessionStarted?.({
          lessonId: String(blockedSession.lessonId),
          sessionId: String(blockedSession.sessionId),
          source: blockedSession.source ?? 'dashboard_progress',
        });
        setError('You already have an active dashboard core skill. Resuming it now.');
      } else {
        setError('Failed to start session');
      }
    } finally {
      setLoadingId('');
    }
  };

  const isLocked = !!activeDashboardSession?.lessonId;
  const isOptionLocked = (lessonId) =>
    isLocked && String(activeDashboardSession.lessonId) !== String(lessonId);
  const getButtonLabel = (lessonId) => {
    if (loadingId === lessonId) return 'Starting...';
    if (activeDashboardSession?.lessonId === lessonId) return 'Resume Core Skill';
    return 'Start Core Skill';
  };

  if (options.length <= 2) {
    return (
      <div className="flex items-center gap-2">
        {options.map((opt) => (
          <div
            key={opt.lessonId}
            className="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white/60 pl-0.5 pr-2.5 py-0.5 max-w-[280px] transition-all duration-200 hover:border-indigo-200 hover:bg-white hover:shadow-sm"
          >
            <button
              type="button"
              onClick={(e) => handleStart(e, opt.lessonId)}
              disabled={loadingId === opt.lessonId || isOptionLocked(opt.lessonId)}
              className="inline-flex items-center gap-1 rounded-full bg-[#5C3BD1] text-white text-[11px] font-medium px-2 py-0.5 max-md:min-h-[44px] max-md:px-3 max-md:text-sm transition-all duration-200 ease-out hover:bg-[#4A2FB8] hover:-translate-y-[2px] hover:shadow-[0_14px_30px_-14px_rgba(92,59,209,0.50)] active:translate-y-0 active:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed motion-reduce:transform-none motion-reduce:transition-none shrink-0 whitespace-nowrap"
              title={
                isOptionLocked(opt.lessonId)
                  ? 'Another dashboard core skill is active. Complete it or wait one hour.'
                  : ''
              }
            >
              <Play className="h-3 w-3 fill-current" />
              {getButtonLabel(opt.lessonId)}
            </button>
            <span
              className="text-xs text-gray-600 truncate ml-1"
              title={opt.title}
            >
              {opt.title}
            </span>
          </div>
        ))}
        {error && <span className="text-[10px] text-red-500 shrink-0">{error}</span>}
        {!error && isLocked ? (
          <span className="text-[10px] text-amber-700 shrink-0">
            One dashboard core skill can run at a time.
          </span>
        ) : null}
      </div>
    );
  }

  return (
    <div className="flex items-center gap-1.5">
      <div className="flex items-center gap-0.5 rounded-full border border-gray-200 bg-white/60 pl-0.5 pr-2 py-0.5 transition-all duration-200 hover:border-indigo-200 hover:bg-white hover:shadow-sm">
        <select
          value={selectedId}
          onClick={(e) => e.stopPropagation()}
          onChange={(e) => { e.stopPropagation(); setSelectedId(e.target.value); }}
          className="border-none bg-transparent text-xs text-gray-600 pl-1.5 pr-1 py-0 focus:outline-none max-w-[150px]"
        >
          <option value="">Core skill...</option>
          {options.map((opt) => (
            <option key={opt.lessonId} value={opt.lessonId}>
              {opt.title}
            </option>
          ))}
        </select>
        {selectedId && (
          <button
            type="button"
            onClick={(e) => handleStart(e, selectedId)}
            disabled={!!loadingId || isOptionLocked(selectedId)}
            className="inline-flex items-center gap-1 rounded-full bg-[#5C3BD1] text-white text-[11px] font-medium px-2 py-0.5 max-md:min-h-[44px] max-md:px-3 max-md:text-sm transition-all duration-200 ease-out hover:bg-[#4A2FB8] hover:-translate-y-[2px] hover:shadow-[0_14px_30px_-14px_rgba(92,59,209,0.50)] active:translate-y-0 active:shadow-none focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed motion-reduce:transform-none motion-reduce:transition-none shrink-0"
            title={
              isOptionLocked(selectedId)
                ? 'Another dashboard core skill is active. Complete it or wait one hour.'
                : ''
            }
          >
            <Play className="h-3 w-3 fill-current" />
            {loadingId ? 'Starting...' : getButtonLabel(selectedId)}
          </button>
        )}
      </div>
      {error && <span className="text-[10px] text-red-500 shrink-0">{error}</span>}
    </div>
  );
}

export default InlineCoreSkills;
